import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  LinearProgress,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Bell, CheckCircle2, AlertCircle, Loader2, X, ClipboardCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDocumentExtraction } from "../context/DocumentExtractionContext";
import type { DocumentExtractionJob } from "../utils/documentExtractionJobs";
import {
  categorizeExtractionJobs,
  isJobCompleted,
  isJobFailed,
  isJobInProgress,
  isJobInReview,
  isJobStuckInQueue,
} from "../utils/documentExtractionJobs";
import { navigateToExtractionJob } from "../utils/documentExtractionNavigation";

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function JobRow({
  job,
  onNavigate,
  onDismiss,
  onMarkRead,
}: {
  job: DocumentExtractionJob;
  onNavigate: () => void;
  onDismiss?: () => void;
  onMarkRead?: () => void;
}) {
  const stuck = isJobStuckInQueue(job);
  const active = isJobInProgress(job);
  const failed = isJobFailed(job);
  const inReview = isJobInReview(job);
  const complete = isJobCompleted(job);

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 1,
        "&:hover": { bgcolor: "#f8fafc" },
        cursor: "pointer",
      }}
      onClick={() => {
        onMarkRead?.();
        onNavigate();
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ pt: 0.25, flexShrink: 0 }}>
          {active && <Loader2 size={14} color="#2563eb" className="spin" />}
          {inReview && !active && <ClipboardCheck size={14} color="#d97706" />}
          {complete && !active && !inReview && <CheckCircle2 size={14} color="#16a34a" />}
          {failed && !stuck && <AlertCircle size={14} color="#dc2626" />}
          {stuck && <AlertCircle size={14} color="#d97706" />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "12px", fontWeight: 600 }} noWrap title={job.name}>
            {job.name}
          </Typography>
          {job.contextLabel && (
            <Typography sx={{ fontSize: "10px", color: "#64748b" }} noWrap>
              {job.contextLabel}
            </Typography>
          )}
          {active && (
            <>
              <Typography sx={{ fontSize: "10px", color: "#2563eb", mt: 0.25 }}>
                {job.progress != null ? `${job.progress}%` : "Extracting…"}
                {job.stage ? ` · ${job.stage}` : ""}
              </Typography>
              <LinearProgress
                variant={job.progress != null ? "determinate" : "indeterminate"}
                value={job.progress ?? 0}
                sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
              />
            </>
          )}
          {!active && (
            <Typography
              sx={{
                fontSize: "10px",
                color: stuck ? "#d97706" : inReview ? "#d97706" : failed ? "#dc2626" : "#94a3b8",
                mt: 0.25,
              }}
            >
              {stuck
                ? "Stuck in queue · tap to retry"
                : inReview
                  ? `Ready for review · ${formatRelativeTime(job.updatedAt)}`
                  : failed
                    ? `Failed · ${formatRelativeTime(job.uploadedAt)}${job.error ? ` · ${job.error}` : ""}`
                    : formatRelativeTime(job.uploadedAt)}
            </Typography>
          )}
        </Box>
        {onDismiss && (
          <IconButton
            size="small"
            sx={{ width: 20, height: 20 }}
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            aria-label="Dismiss"
          >
            <X size={12} />
          </IconButton>
        )}
      </Stack>
    </Box>
  );
}

export default function DocumentExtractionBell() {
  const navigate = useNavigate();
  const {
    jobs,
    activeCount,
    unreadCount,
    badgeCount,
    dismissJob,
    markAllRead,
    markJobRead,
    isBootstrapping,
    stuckCheckTick,
  } = useDocumentExtraction();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { inProgress, inReview, completed, failed } = useMemo(
    () => categorizeExtractionJobs(jobs),
    [jobs, stuckCheckTick]
  );

  const hasJobs = inProgress.length + inReview.length + completed.length + failed.length > 0;

  return (
    <>
      <style>{`
        @keyframes docExtractionSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin { animation: docExtractionSpin 1.2s linear infinite; }
      `}</style>
      <Tooltip title="Document extraction status" arrow>
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            height: 34,
            width: 34,
            color: "#374151",
            bgcolor: "#f3f4f6",
            "&:hover": { bgcolor: "#e5e7eb" },
          }}
          aria-label="Document extraction notifications"
        >
          <Badge
            badgeContent={badgeCount}
            color={activeCount > 0 ? "primary" : unreadCount > 0 ? "error" : "default"}
            invisible={badgeCount === 0}
            max={99}
          >
            <Bell size={18} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: { width: 320, maxHeight: 420, mt: 1, borderRadius: 2 },
        }}
      >
        <Box sx={{ px: 1.5, py: 1.25, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "13px" }}>
            Your uploads
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" sx={{ fontSize: "10px", textTransform: "none" }} onClick={markAllRead}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {isBootstrapping && (
          <Typography sx={{ px: 1.5, py: 1, fontSize: "11px", color: "#64748b" }}>
            Restoring extraction status…
          </Typography>
        )}

        {hasJobs && (
          <Box sx={{ maxHeight: 340, overflowY: "auto" }}>
        {inProgress.length > 0 && (
          <>
            <Typography
              variant="overline"
              sx={{ px: 1.5, pt: 1, fontSize: "10px", color: "#64748b", display: "block" }}
            >
              In progress ({inProgress.length})
            </Typography>
            {inProgress.map((job) => (
              <JobRow
                key={job.documentId}
                job={job}
                onNavigate={() => {
                  setAnchorEl(null);
                  navigateToExtractionJob(job, navigate);
                }}
              />
            ))}
          </>
        )}

        {inReview.length > 0 && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Typography
              variant="overline"
              sx={{ px: 1.5, pt: 0.5, fontSize: "10px", color: "#d97706", display: "block" }}
            >
              In review ({inReview.length})
            </Typography>
            {inReview.map((job) => (
              <JobRow
                key={job.documentId}
                job={job}
                onMarkRead={() => markJobRead(job.documentId)}
                onDismiss={() => dismissJob(job.documentId)}
                onNavigate={() => {
                  setAnchorEl(null);
                  navigateToExtractionJob(job, navigate, true);
                }}
              />
            ))}
          </>
        )}

        {completed.length > 0 && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Typography
              variant="overline"
              sx={{ px: 1.5, pt: 0.5, fontSize: "10px", color: "#64748b", display: "block" }}
            >
              Completed ({completed.length})
            </Typography>
            {completed.map((job) => (
              <JobRow
                key={job.documentId}
                job={job}
                onMarkRead={() => markJobRead(job.documentId)}
                onDismiss={() => dismissJob(job.documentId)}
                onNavigate={() => {
                  setAnchorEl(null);
                  navigateToExtractionJob(job, navigate);
                }}
              />
            ))}
          </>
        )}

        {failed.length > 0 && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Typography
              variant="overline"
              sx={{ px: 1.5, pt: 0.5, fontSize: "10px", color: "#dc2626", display: "block" }}
            >
              Failed ({failed.length})
            </Typography>
            {failed.map((job) => (
              <JobRow
                key={job.documentId}
                job={job}
                onMarkRead={() => markJobRead(job.documentId)}
                onDismiss={() => dismissJob(job.documentId)}
                onNavigate={() => {
                  setAnchorEl(null);
                  navigateToExtractionJob(job, navigate);
                }}
              />
            ))}
          </>
        )}
          </Box>
        )}

        {!hasJobs && !isBootstrapping && (
          <Typography sx={{ px: 1.5, py: 2, fontSize: "12px", color: "#94a3b8", textAlign: "center" }}>
            No recent document extractions
          </Typography>
        )}
      </Popover>
    </>
  );
}
